import { Box, Typography } from '@mui/material';
import React, { useEffect } from 'react';

import { useInstallationGuide } from '../../../hooks/useInstallationGuide';
import { CORE_MODEL_INFO } from '../../../utils/coreModel';
import { GuidanceSection } from '../GuidanceSection';
import { WizardNavigation } from '../WizardNavigation';

/** Path to the checkmark icon */
const CHECKMARK_ICON_PATH = '/assets/images/installation-guide/checkmark-circle.svg';

/** Static asset paths */
const POWER_ADAPTER_PATH = '/assets/images/installation-guide/power-adapter.png';
const NETWORK_CABLE_PATH = '/assets/images/installation-guide/network-cable.png';

/**
 * BoxContentsStep - Slide 2.3: Box Contents
 * 
 * Shows the user what should be in the box:
 * - The Core device (model-specific image)
 * - Power adapter
 * - Network cable
 * 
 * Includes a chatbot help button for missing/broken items.
 */
export const BoxContentsStep: React.FC = () => {
  const { deviceModel, goToStep, setMainStepId } = useInstallationGuide();

  useEffect(() => {
    setMainStepId('step_boxContents');
  }, [setMainStepId]);

  const handleReady = () => {
    goToStep('placement');
  };

  // Get device image based on model, fallback to Pro if unknown
  const deviceInfo = deviceModel
    ? { src: CORE_MODEL_INFO[deviceModel].imageSrc, labelKey: CORE_MODEL_INFO[deviceModel].full }
    : { src: CORE_MODEL_INFO.Pro.imageSrc, labelKey: CORE_MODEL_INFO.Pro.full };

  // Box contents items
  const boxItems = [
    {
      image: deviceInfo.src,
      label: __('$[model] x1', { model: __(deviceInfo.labelKey) }),
      alt: __(deviceInfo.labelKey),
    },
    {
      image: POWER_ADAPTER_PATH,
      label: __('Power adapter x1'),
      alt: __('Power adapter'),
    },
    {
      image: NETWORK_CABLE_PATH,
      label: __('Network cable x1'),
      alt: __('Network cable'),
    },
  ];

  const guidanceButtons = [
    { label: __('One or more items is missing or broken'), flowId: 'missing-broken' },
  ];

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h3" component="h1" sx={{ mb: 1, textAlign: 'center' }}>
        {__('Confirm box contents.')}
      </Typography>

      <Typography variant="h6" sx={{ mb: 3, textAlign: 'left' }}>
        {__("Here's what should be in the box:")}
      </Typography>

      {/* Box contents list */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          py: 2,
          mb: 2,
        }}
      >
        {boxItems.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 2,
              borderBottom: index < boxItems.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            {/* Item image */}
            <Box
              sx={{
                width: 80,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={item.image}
                alt={item.alt}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>

            {/* Checkmark and label */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
              <Box
                component="img"
                src={CHECKMARK_ICON_PATH}
                alt=""
                sx={{
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {item.label}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Guidance section with chatbot button */}
      <GuidanceSection
        buttons={guidanceButtons}
      />

      <WizardNavigation
        primaryLabel={__("I'm ready to start")}
        onPrimary={handleReady}
      />
    </Box>
  );
};
